import * as React from 'react';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import { styled, SxProps, Theme } from '@mui/material/styles';
import {
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';

import ExpandChevron from '@mui/icons-material/KeyboardArrowDown';
import { Fragment, useState } from 'react';
import LayupTextIcon from './LayupTextIcon';
import dayjs from 'dayjs';

import LayUpBoxIcon from './LayUpIcon';
import { LayUpStatuses } from '@src/constants/LayupStatuses';

const style: SxProps<Theme> = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: 3,
  width: '50vw',
  whiteSpace: 'nowrap',
};

const CustomTableContainer = styled(TableContainer)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,

  '& .MuiTableCell-root': {
    paddingTop: 0,
    paddingBottom: 0,
  },
}));

export type GroupedLayupsOperatingList = {
  status: LayUpStatuses;
  consultantFullName: string;
  procedureDate: Date;
  operatingList: string;

  layUps: {
    layUpId: string;
    status: LayUpStatuses;
    procedureName: string;
    totalQuantity: number;
    pickedQuantity: number;
    missingQuantity: number;

    items: {
      name: string;
      categoryName: string;
      quantity: number;
      isMissing: boolean;
    }[];
  }[];
};

interface Props {
  open: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  procedures?: GroupedLayupsOperatingList;
}

export function ProceduresModal({ open, onClose, procedures }: Props) {
  const [expandId, setExpandId] = useState<string | null>(null);

  const toggleExpand = (id: string) => {
    setExpandId((prev) => (prev === id ? null : id));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}>
      <Fade in={open}>
        <Box sx={{ ...style }}>
          <Stack direction={'row'} gap={2} px={3} pt={3} pb={3.2}>
            <LayUpBoxIcon status={procedures?.status} />

            <Stack>
              <Typography variant="subtitle1">
                {procedures?.consultantFullName}
              </Typography>
              <Typography variant="body1" color={'text.secondary'}>
                {dayjs(procedures?.procedureDate).format('ddd D MMMM YYYY')} -{' '}
                {procedures?.operatingList}
              </Typography>
            </Stack>
          </Stack>

          <CustomTableContainer sx={{ borderTop: 1, borderColor: 'divider' }}>
            <Table>
              <TableBody>
                {procedures?.layUps.map((procedure, index) => (
                  <Fragment key={procedure.layUpId}>
                    <TableRow>
                      <TableCell sx={{ width: '100%' }}>
                        <Typography variant="subtitle2">
                          {procedure.procedureName}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <LayupTextIcon status={procedure.status} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {procedure.pickedQuantity}/{procedure.totalQuantity}{' '}
                          items
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="subtitle2"
                          color={
                            procedure.missingQuantity > 0
                              ? 'error.dark'
                              : 'text.disabled'
                          }>
                          {procedure.missingQuantity} items missing
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {procedure.missingQuantity > 0 && (
                          <IconButton
                            onClick={() => {
                              toggleExpand(procedure.layUpId);
                            }}>
                            <ExpandChevron />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                    {expandId === procedure.layUpId &&
                      procedure.items.map((i) => (
                        <TableRow>
                          <TableCell>
                            <Typography variant="body1">{i.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color={'text.secondary'}>
                              {i.categoryName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color={'text.secondary'}>
                              Qty: {i.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="error.dark">
                              {i.quantity} items missing
                            </Typography>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </CustomTableContainer>
          <Box
            sx={{ p: 3, display: 'flex', justifyContent: 'flex-end' }}
            onClick={onClose}>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}

export default ProceduresModal;
